import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Save } from 'lucide-react';
import api from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import Button from './Button';
import Input from './Input';
import type { LivingTerms } from '../../hooks/useLivingTerms';

interface Props {
  terms: LivingTerms;
  onMessage: (msg: { type: 'success' | 'error'; text: string }) => void;
}

/**
 * Noms des deux membres du foyer. Une fois les comptes liés, chacun ne modifie
 * que son propre nom : celui de l'autre est grisé.
 */
export default function HouseholdMembersCard({ terms, onMessage }: Props) {
  const { t } = useTranslation('profile');
  const { user, setUser } = useAuthStore();
  const hasPartner = user?.hasPartner || false;

  const [coupleProfile, setCoupleProfile] = useState({
    partner1FirstName: user?.partner1FirstName || '',
    partner1LastName: user?.partner1LastName || '',
    partner2FirstName: user?.partner2FirstName || '',
    partner2LastName: user?.partner2LastName || '',
  });
  const [isSaving, setIsSaving] = useState(false);

  // Synchroniser l'état local quand le store user est mis à jour (ex: fetch DashboardLayout)
  useEffect(() => {
    setCoupleProfile({
      partner1FirstName: user?.partner1FirstName || '',
      partner1LastName: user?.partner1LastName || '',
      partner2FirstName: user?.partner2FirstName || '',
      partner2LastName: user?.partner2LastName || '',
    });
  }, [user?.partner1FirstName, user?.partner1LastName, user?.partner2FirstName, user?.partner2LastName]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await api.put('/user/couple-profile', coupleProfile);
      if (response.data.success) {
        // Synchroniser aussi le profil personnel dans le store
        const isPartner1 = user?.coupleRole === 'owner';
        const myFirstName = isPartner1 ? coupleProfile.partner1FirstName : coupleProfile.partner2FirstName;
        const myLastName = isPartner1 ? coupleProfile.partner1LastName : coupleProfile.partner2LastName;
        setUser({ ...user!, ...response.data.data, firstName: myFirstName, lastName: myLastName });
        onMessage({ type: 'success', text: t('personalInfo.coupleUpdated', { group: terms.groupName }) });
      }
    } catch {
      onMessage({ type: 'error', text: t('updateError', { ns: 'common' }) });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="card">
      <h3 className="text-base font-semibold text-gray-900 mb-4">{t('couple.infoTitle', { possessive: terms.groupPossessive })}</h3>
      <p className="text-sm text-gray-500 mb-4">
        {t('couple.infoDescription', { partner: terms.partnerSingular })}
      </p>
      <div className="space-y-4">
        {/* Partenaire 1 — grisé pour partner2 si compte lié */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label={t('couple.firstNameLabel', { partner: terms.partner1Default.toLowerCase() })}
            value={coupleProfile.partner1FirstName}
            onChange={(e) => setCoupleProfile({ ...coupleProfile, partner1FirstName: e.target.value })}
            disabled={hasPartner && user?.coupleRole !== 'owner'}
          />
          <Input
            label={t('couple.lastNameLabel', { partner: terms.partner1Default.toLowerCase() })}
            value={coupleProfile.partner1LastName}
            onChange={(e) => setCoupleProfile({ ...coupleProfile, partner1LastName: e.target.value })}
            disabled={hasPartner && user?.coupleRole !== 'owner'}
          />
        </div>
        {/* Partenaire 2 — grisé pour partner1 si compte lié */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label={t('couple.firstNameLabel', { partner: terms.partner2Default.toLowerCase() })}
            value={coupleProfile.partner2FirstName}
            onChange={(e) => setCoupleProfile({ ...coupleProfile, partner2FirstName: e.target.value })}
            disabled={hasPartner && user?.coupleRole !== 'partner'}
          />
          <Input
            label={t('couple.lastNameLabel', { partner: terms.partner2Default.toLowerCase() })}
            value={coupleProfile.partner2LastName}
            onChange={(e) => setCoupleProfile({ ...coupleProfile, partner2LastName: e.target.value })}
            disabled={hasPartner && user?.coupleRole !== 'partner'}
          />
        </div>
        <div className="pt-2">
          <Button
            variant="primary"
            leftIcon={<Save className="w-4 h-4" />}
            onClick={handleSave}
            isLoading={isSaving}
          >
            {t('personalInfo.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
